import { useAppSelector } from "@app/hooks";
import PDFFooter from "@components/pdf-partials/PDFFooter";
import PDFHeader from "@components/pdf-partials/PDFHeader";
import { useTransformedImageUrl } from "@components/TransformedImage";
import { EmeraldIcon } from "@emerald-react/icon";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetMediaByIdsQuery } from "@services/media";
import { renderUrlOrValue } from "@shared/utils";
import SurveyPDFPage from "@survey-export/SurveyPDFPage";
import type { LeaseCompFieldRule } from "@survey-types/survey.core.types";
import type {
  LeaseComp,
  LeaseCompFields,
} from "@survey-types/survey.leaseComp.types";
import { selectLeaseCompMediaFiles } from "@survey/leaseCompsSelectors";
import { selectCurrentSurvey } from "@survey/surveySelectors";
import classNames from "classnames";

/**
 * Format field values for display
 */
const formatFieldValue = (
  value: string | undefined,
  fieldKey: string,
): string => {
  if (!value || value === "") return "—";

  // Only apply numeric formatting when the value is a plain number string.
  // Values that already contain commas, units, or other non-numeric characters
  // (e.g. "100,423 SF") must be returned as-is to avoid truncation via parseFloat.
  const isPlainNumber = /^-?\d+(\.\d+)?$/.test(value.trim());

  // Format currency fields
  if (
    (fieldKey.includes("Rent") && fieldKey !== "freeRent") ||
    fieldKey.includes("Allowance") ||
    fieldKey.includes("opex")
  ) {

    if (isPlainNumber) {
      const numValue = parseFloat(value);
      return `$${numValue.toLocaleString()}`;
    }
  }

  // Format size fields
  if (fieldKey.includes("Size") || fieldKey.includes("area")) {
    if (isPlainNumber) {
      const numValue = parseFloat(value);
      return numValue.toLocaleString();
    }
  }

  return value;
};

export interface LeaseComparisonPageData {
  leaseComps: LeaseComp[];
  fields: LeaseCompFieldRule[];
  hasImages?: boolean;
  isFirstPageOfLeaseCompSet?: boolean;
}

export interface LeaseComparisonPageProps {
  pageData: LeaseComparisonPageData;
  pageIndex: number;
  maxLeaseComps: number;
  leaseCompOrders: Record<string, string>;
  startingRowIndex: number;
  mode: "Landscape" | "Portrait";
}

/**
 * Lease Comparison Page Component
 *
 * Displays lease comparison data in a PDF-ready format.
 * Structure follows PropertyComparisonPage pattern with:
 * - PDF page wrapper with header and footer
 * - Responsive grid layout for multiple lease comps
 * - Field-based row display with paginated field subset
 * - Height-aware pagination ensures fields fit on page
 * - Continuous row striping across pages via startingRowIndex
 */
/**
 * Sub-component that handles S3 → blob URL transformation per lease comp image.
 * Must be a separate component so the hook is called at component level, not inside a map.
 */
const LeaseCompImageCell = ({
  imageUrl,
  order,
  pinColor,
}: {
  imageUrl: string | undefined;
  order: string;
  pinColor?: string;
}) => {
  const { transformedImageUrl } = useTransformedImageUrl(imageUrl);

  return (
    <div
      style={{
        backgroundImage: transformedImageUrl
          ? `url(${transformedImageUrl})`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        padding: "2px",
        height: "84px",
      }}
      className={classNames("flex items-center justify-center relative", {
        "bg-gray-50": !transformedImageUrl,
      })}
    >
      {!transformedImageUrl && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400">
          <EmeraldIcon
            icon="business"
            type="filled"
            variant="icon"
            className="text-5xl!"
          />
        </div>
      )}
      <span
        style={{
          backgroundColor: pinColor || "#E47444",
          border: "1px solid white",
          margin: "2px",
        }}
        className="lcNum absolute top-0 left-0 shrink-0 inline-flex items-center justify-center w-5 h-5 text-white text-xs z-10"
      >
        {order}
      </span>
    </div>
  );
};

// Keys merged into the address row (not shown as separate rows)
const addressLine2Keys = [
  "propertyCity",
  "propertyState",
  "propertyPostalCode",
] as const;
const addressLine1Keys = ["propertyAddress"] as const;

const LeaseComparisonPage = ({
  pageData,
  pageIndex,
  maxLeaseComps,
  leaseCompOrders,
  startingRowIndex,
  mode,
}: LeaseComparisonPageProps) => {
  const isFirstPage = pageIndex === 0;
  const showHeader = isFirstPage || pageData.isFirstPageOfLeaseCompSet;
  const layoutClass = showHeader ? "pdf-grid-layout" : "pdf-grid-split-layout";
  const gridTemplateColumns = `150px repeat(${maxLeaseComps}, 1fr)`;
  // Character limit for line splitting, aligned with property comparison
  const charsPerLine = mode === "Landscape" ? 18 : 25;

  // Determine which address-related field keys are active on this page
  const availableAddressKeys = pageData.fields
    .filter((field) =>
      ([...addressLine1Keys, ...addressLine2Keys] as string[]).includes(
        field.keyName,
      ),
    )
    .map((f) => f.keyName);

  // Whether the name field (propertyName) is present on this page
  const isNameFieldVisible = pageData.fields.some(
    (field) => field.keyName === "propertyName",
  );

  // Always strip city/state/postal out as individual rows — they are always merged
  const filteredFields = pageData.fields.filter(
    (field) => !(addressLine2Keys as readonly string[]).includes(field.keyName),
  );

  const hasAddressRow = pageData.fields.some(
    (f) => f.keyName === "propertyAddress",
  );
  const addressLine2Fields = pageData.fields.filter((f) =>
    (addressLine2Keys as readonly string[]).includes(f.keyName),
  );

  // If propertyAddress is off but city/state/postal are on, inject a synthetic
  // address row so the merged values still display (mirrors property comparison)
  const displayFields: LeaseCompFieldRule[] = (() => {
    if (hasAddressRow || addressLine2Fields.length === 0) {
      return filteredFields;
    }
    // Insert synthetic row at the position the first address-line-2 field occupied
    const firstLine2Index = pageData.fields.findIndex((f) =>
      (addressLine2Keys as readonly string[]).includes(f.keyName),
    );
    const insertionIndex = pageData.fields
      .slice(0, firstLine2Index)
      .filter(
        (f) => !(addressLine2Keys as readonly string[]).includes(f.keyName),
      ).length;
    const syntheticField: LeaseCompFieldRule = {
      key: "synthetic-propertyAddress",
      keyName: "propertyAddress",
      displayName: "Address",
      isActive: true,
      order: addressLine2Fields[0].order,
    };
    return [
      ...filteredFields.slice(0, insertionIndex),
      syntheticField,
      ...filteredFields.slice(insertionIndex),
    ];
  })();

  /**
   * Returns the display value for a lease comp field.
   * Mirrors the name/address logic from property comparison:
   * - propertyName: falls back to propertyAddress when empty
   * - propertyAddress: shows combined address (address + city, state postal),
   *   omitting address line 1 when it was already shown via the name fallback
   */
  const getLeaseCompFieldValue = (
    leaseComp: LeaseComp,
    keyName: string,
  ): string => {
    const fields = leaseComp.leaseCompFields ?? ({} as LeaseCompFields);

    if (keyName === "propertyName") {
      const name = fields.propertyName;
      if (!name || name === "") {
        const address = fields.propertyAddress;
        return address ? address.replace(/<|>/g, "") : "—";
      }
      return name.replace(/<|>/g, "");
    }

    if (keyName === "propertyAddress") {
      // Exclude address line 1 when it was already shown in the name row
      const shouldExcludeAddress1 =
        isNameFieldVisible &&
        (!fields.propertyName || fields.propertyName === "");

      const addressLine1 =
        !shouldExcludeAddress1 &&
        availableAddressKeys.includes("propertyAddress")
          ? (fields.propertyAddress?.replace(/<|>/g, "") ?? "")
          : "";

      const stateZip = [
        availableAddressKeys.includes("propertyState")
          ? (fields.propertyState ?? "")
          : "",
        availableAddressKeys.includes("propertyPostalCode")
          ? (fields.propertyPostalCode ?? "")
          : "",
      ]
        .filter(Boolean)
        .join(" ");

      const addressLine2Parts = [
        availableAddressKeys.includes("propertyCity")
          ? (fields.propertyCity ?? "")
          : "",
        stateZip,
      ]
        .filter(Boolean)
        .join(", ");

      const addressParts = [addressLine1, addressLine2Parts].filter(Boolean);
      return addressParts.length > 0 ? addressParts.join("\n") : "—";
    }

    const value = fields[keyName as keyof LeaseCompFields];
    return formatFieldValue(value as string, keyName);
  };

  // Fetch media for all lease comps on this page
  const leaseCompIds = pageData.leaseComps.map((lc) => lc.leaseCompId);
  useGetMediaByIdsQuery(
    leaseCompIds.length > 0
      ? { ids: leaseCompIds, ownerType: "LEASE_COMP" }
      : skipToken,
  );

  // Read lease comp media from leaseComps slice
  const allMediaFiles = useAppSelector(selectLeaseCompMediaFiles);
  const survey = useAppSelector(selectCurrentSurvey);
  const showPropertyPhoto = survey?.showPropertyPhoto ?? true;

  // Helper to get the first image URL for a lease comp
  const getLeaseCompImageUrl = (leaseCompId: string): string | undefined => {
    const mediaFiles = allMediaFiles?.[leaseCompId]?.images;
    if (!mediaFiles || mediaFiles.length === 0) return undefined;

    const firstImage = mediaFiles.find(
      (img) => !img.isHidden && img.cloudfrontUrl,
    );
    return firstImage?.cloudfrontUrl;
  };

  // Calculate grid template columns
  // const gridTemplateColumns = `200px repeat(${maxLeaseComps}, 1fr)`;

  // Pre-calculate empty columns array
  const emptyColumnsCount = maxLeaseComps - pageData.leaseComps.length;
  const emptyColumns = Array.from({ length: emptyColumnsCount });

  // Check if there are no lease comps to display
  const hasNoLeaseComps =
    !pageData.leaseComps || pageData.leaseComps.length === 0;

  return (
    <SurveyPDFPage mode="PDF">
      <div className={`w-full h-full bg-white ${layoutClass}`}>
        {/* Conditional header rendering - show on first page OR when starting a new lease comp set */}
        {showHeader && <PDFHeader title="Lease Comps" />}

        <div className="overflow-hidden min-h-0 pdf-grid-content main-pdf-content">
          {hasNoLeaseComps ? (
            /* No Lease Comps State */
            <div className="flex items-center justify-center min-h-100 px-4">
              <div className="text-center">
                <p className="text-gray-600 mx-auto leading-relaxed">
                  In order to see lease comparables, please add lease comps with
                  information associated.
                </p>
              </div>
            </div>
          ) : (
            <div className="min-w-full px-8">
              {/* Lease Comp Header Row - Property Names (shown when header is shown) */}
              {showHeader && (
                <div
                  className={classNames("grid", {
                    "pt-4": showHeader,
                  })}
                  style={{ gridTemplateColumns }}
                >
                  <div className="px-2 py-3 font-semibold text-sm text-gray-700" />

                  {pageData.leaseComps.map((leaseComp) => {
                    const order = leaseCompOrders[leaseComp.leaseCompId];
                    const leaseCompImageUrl = getLeaseCompImageUrl(
                      leaseComp.leaseCompId,
                    );
                    return (
                      <div
                        key={leaseComp.leaseCompId}
                        className="px-2 font-semibold text-sm text-gray-900 wrap-break-word relative"
                      >
                        {showPropertyPhoto ? (
                          <LeaseCompImageCell
                            imageUrl={leaseCompImageUrl}
                            order={order}
                            pinColor={leaseComp.mapData?.iconColor || "#E47444"}
                          />
                        ) : (
                          <span
                            style={{
                              backgroundColor:
                                leaseComp.mapData?.iconColor || "#E47444",
                              border: "1px solid white",
                            }}
                            className="lcNum shrink-0 inline-flex items-center justify-center w-5 h-5 text-white text-xs"
                          >
                            {order}
                          </span>
                        )}
                      </div>
                    );
                  })}

                  {/* Fill empty columns if less than max lease comps */}
                  {emptyColumns.map((_, index) => (
                    <div key={`empty-header-${index}`} className="px-2" />
                  ))}
                </div>
              )}

              {/* Lease Comp Detail Rows - Paginated Fields */}
              {displayFields.map((field, fieldIndex) => {
                const rowIndex = startingRowIndex + fieldIndex;

                return (
                  <div
                    key={field.key}
                    className={classNames("grid py-1", {
                      "bg-gray-50": rowIndex % 2 === 1,
                      "bg-white": rowIndex % 2 === 0,
                    })}
                    style={{ gridTemplateColumns }}
                  >
                    {/* Field Label */}
                    <div className="text-tiny text-data break-normal wrap-break-word hyphens-auto pl-2">
                      {field.displayName}
                    </div>

                    {/* Field Values for each lease comp */}
                    {pageData.leaseComps.map((leaseComp) => {
                      const formattedValue = getLeaseCompFieldValue(
                        leaseComp,
                        field.keyName,
                      );

                      return (
                        <div
                          key={`${leaseComp.leaseCompId}-${field.keyName}`}
                          className={classNames(
                            "text-tiny px-2 leading-3.5 break-normal wrap-break-word hyphens-auto text-data",
                            {
                              "font-semibold": field.keyName === "propertyName",
                            },
                          )}
                        >
                          {renderUrlOrValue(
                            formattedValue,
                            field.keyName,
                            charsPerLine,
                          )}
                        </div>
                      );
                    })}

                    {/* Fill empty columns if less than max lease comps */}
                    {emptyColumns.map((_, index) => (
                      <div
                        key={`empty-${field.keyName}-${index}`}
                        className="px-2"
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <PDFFooter />
      </div>
    </SurveyPDFPage>
  );
};

export default LeaseComparisonPage;
